/**
 * Helper di interrogazione dei permessi.
 *
 * Replica la stessa regola di precedenza usata dal backend
 * (`core/auth/permessi.py`):
 *   1. MAITIME_STAFF → True sempre.
 *   2. Se la chiave è in `permessi_espliciti[tipo]` → ritorna quel valore
 *      (cope preset ruolo e override utente, mergiati dal backend).
 *   3. Se il ruolo è configurato → False (default deny).
 *   4. Altrimenti → True (default legacy, ruoli mai configurati).
 *
 * Se `perms` è undefined (loading o errore /me/permessi), opto per il
 * comportamento più conservativo per il cliente: **deny**. Mostriamo
 * skeleton/spinner finché perms non è caricato.
 */

import type { PermessiUtente, RisorsaTipo } from "@/types/api";

export function hasPermesso(
  perms: PermessiUtente | undefined,
  tipo: RisorsaTipo,
  key: string
): boolean {
  if (!perms) return false;
  if (perms.is_maitime_staff) return true;
  const esplicito = perms.permessi_espliciti?.[tipo]?.[key];
  if (esplicito !== undefined) return esplicito;
  return !perms.ruolo_configurato;
}

/**
 * Permesso ESPLICITO per le azioni operative sensibili (tipo `azione`).
 *
 * A differenza di `hasPermesso`, applica default-DENY anche ai ruoli non
 * configurati (niente legacy allow-all): l'azione è consentita solo a
 * MAITIME_STAFF o a chi ha un grant esplicito True. Speculare al backend
 * `Permessi.is_concesso_esplicito`.
 */
export function isAzioneConcessa(
  perms: PermessiUtente | undefined,
  key: string
): boolean {
  if (!perms) return false;
  if (perms.is_maitime_staff) return true;
  return perms.permessi_espliciti?.azione?.[key] === true;
}

/**
 * Permesso di una **sezione di modulo** (es. `m3.partenze`).
 *
 * Regola: **la sezione eredita dal modulo, salvo override esplicito.** Nessun
 * grant → vale il permesso del modulo che la contiene; un `false` esplicito la
 * toglie solo a quel ruolo.
 *
 * L'eredità non è un dettaglio: se le sezioni fossero chiavi controllate
 * normali, per i ruoli **configurati** il default è deny — e il giorno del
 * deploy chiunque abbia M3 perderebbe tutte le sue sezioni. Così invece il
 * cambiamento è puramente additivo: prima del primo override non cambia niente
 * per nessuno. Da tenere quando si aggiungeranno le sezioni di M1/M2/M4.
 */
export function hasPermessoSezione(
  perms: PermessiUtente | undefined,
  sezioneKey: string,
  moduloKey: string
): boolean {
  if (!perms) return false;
  if (perms.is_maitime_staff) return true;
  const esplicito = perms.permessi_espliciti?.nav_voce?.[sezioneKey];
  if (esplicito !== undefined) return esplicito;
  return hasPermesso(perms, "modulo", moduloKey);
}

/** Scorciatoia: può caricare i dati operativi del modulo M1 (upload Excel). */
export function canUploadDati(perms: PermessiUtente | undefined): boolean {
  return isAzioneConcessa(perms, "upload_dati");
}
